import React, { useEffect, useState } from 'react';
import Select from 'react-select';
// Common Components (Corrected Paths)
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
// API & Utils (Corrected Paths)
import { createRate, fetchRate, updateRate } from '../../../api/RatesAPI';
import { getProducts } from '../../../api/ProductsAPI';
import { getUoms } from '../../../api/UomsAPI';
import { getRateTypes } from '../../../api/RateTypesAPI';
import { getHoas } from '../../../api/HoasAPI'; // Import Hoas API
import { handleApiError } from '../../../api/risapi';
// Hooks & Helpers
import { useNavigate, useParams } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../../commonfunctions';
// Date Picker
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';

const RatesForm = ({ formType, currentRateId }) => {
    const navigate = useNavigate();
    const [currentRate, setCurrentRate] = useState({
        product_id: null,
        uom_id: null,
        rate_type_id: null,
        base_price: '', // Changed from price
        with_effect_from: new Date(),
        adjustments: [], // Added state for adjustments
    });
    const [currentRateError, setCurrentRateError] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);
    const [loadingRateData, setLoadingRateData] = useState(false);
    const [saving, setSaving] = useState(false);

    // Dropdown Options
    const [productOptions, setProductOptions] = useState([]);
    const [uomOptions, setUomOptions] = useState([]);
    const [filteredUomOptions, setFilteredUomOptions] = useState([]);
    const [rateTypeOptions, setRateTypeOptions] = useState([]);
    const [gstHoaOptions, setGstHoaOptions] = useState([]); // State for GST HOAs

    // --- Fetch Dropdown Data ---
    useEffect(() => {
        let isMounted = true;
        setLoadingDropdowns(true);
        const fetchDropdowns = async () => {
            try {
                const [productResponse, uomResponse, rateTypeResponse, hoaResponse] = await Promise.all([
                    new Promise((resolve) => getProducts({ paginate: false }, resolve)),
                    new Promise((resolve) => getUoms({ paginate: false }, resolve)),
                    new Promise((resolve) => getRateTypes({ paginate: false }, resolve)),
                    new Promise((resolve) => getHoas({ paginate: false, is_gst_head: true }, resolve)), // Fetch only GST HOAs
                ]);

                if (!isMounted) return;

                // Process Products
                if (productResponse.products && Array.isArray(productResponse.products)) {
                     setProductOptions(productResponse.products.map(p => ({ value: p.id, label: `${p.name} ${p.code ? `(${p.code})` : ''}`, base_uom_id: p.base_uom_id, product_units: p.product_units || [] })));
                 } else { throw new Error('Invalid product data'); }
                // Process UOMs
                if (uomResponse.uoms && Array.isArray(uomResponse.uoms)) {
                     const options = uomResponse.uoms.map(u => ({ value: u.id, label: `${u.name} (${u.short_name})` }));
                     setUomOptions(options);
                     // ** FIX: Set filtered options initially **
                     setFilteredUomOptions(options);
                 } else { throw new Error('Invalid UOM data'); }
                // Process Rate Types
                if (rateTypeResponse.rate_types && Array.isArray(rateTypeResponse.rate_types)) {
                    setRateTypeOptions(rateTypeResponse.rate_types.map(rt => ({ value: rt.id, label: rt.name })));
                 } else { throw new Error('Invalid Rate Type data'); }
                // Process GST HOAs
                if (hoaResponse.hoas) {
                    const hoasData = Array.isArray(hoaResponse.hoas) ? hoaResponse.hoas : (hoaResponse.hoas.data || []);
                    setGstHoaOptions(hoasData.map(h => ({ value: h.id, label: h.name })));
                 } else { throw new Error('Invalid HOA data'); }

            } catch (error) {
                 if (!isMounted) return;
                console.error("Error fetching dropdown data:", error);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load required master data. Please try again.' });
                setShowToaster(true);
                setProductOptions([]); setUomOptions([]); setFilteredUomOptions([]); setRateTypeOptions([]); setGstHoaOptions([]); // Clear all on error
            } finally {
                 if (isMounted) setLoadingDropdowns(false);
            }
        };
        fetchDropdowns();
        return () => { isMounted = false; };
    }, []);

    // --- Fetch Rate Data in Edit Mode ---
    useEffect(() => {
        if (loadingDropdowns || formType === 'create' || !currentRateId) {
            if (formType === 'create') setLoadingRateData(false);
            return;
        }
        let isMounted = true;
        setCurrentRate({ product_id: null, uom_id: null, rate_type_id: null, base_price: '', with_effect_from: new Date(), adjustments: [] });
        setCurrentRateError({});
        setLoadingRateData(true);

        fetchRate(currentRateId, (response) => {
            if (!isMounted) return;
            setLoadingRateData(false);
            if (response.rate) {
                const fetchedRate = response.rate;
                let effectiveDate = new Date();
                if (fetchedRate.with_effect_from) {
                    try {
                        effectiveDate = parseISO(fetchedRate.with_effect_from + 'T00:00:00');
                        if (isNaN(effectiveDate)) effectiveDate = new Date();
                    } catch (e) { effectiveDate = new Date(); }
                }
                const adjustmentsFromServer = fetchedRate.adjustments;
                const formattedAdjustments = Array.isArray(adjustmentsFromServer)
                    ? adjustmentsFromServer.map(adj => ({
                         id: adj.id, hoa_id: adj.hoa_id,
                         percentage: adj.percentage ?? '', amount: adj.amount ?? '',
                         is_subtraction: !!adj.is_subtraction, description: adj.description || ''
                    })) : [];
                setCurrentRate({
                    ...fetchedRate,
                    base_price: fetchedRate.base_price || '',
                    with_effect_from: effectiveDate,
                    adjustments: formattedAdjustments,
                });
                // ** FIX: Filter UOMs after product_id is set in state **
                filterUomsForProduct(fetchedRate.product_id);
            } else {
                 handleApiError(response, () => {});
                 setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Rate data.' });
                 setShowToaster(true);
                 // navigate('/franchisee_user/masters/rates'); // Optional: redirect
            }
        });
        return () => { isMounted = false; };
    }, [formType, currentRateId, loadingDropdowns, navigate]); // Dependencies remain the same


// --- Filter UOMs based on selected Product ---
     const filterUomsForProduct = (selectedProductId) => {
         // Ensure dropdown options are loaded before attempting to filter
         if (loadingDropdowns || !Array.isArray(productOptions) || productOptions.length === 0 || !Array.isArray(uomOptions) || uomOptions.length === 0) {
             console.warn("Dropdown options not ready for UOM filtering.");
             // Keep the current filter or reset to all UOMs? Resetting might be safer.
             setFilteredUomOptions(uomOptions || []); // Use loaded UOMs or empty array
             return;
         }

         const selectedProduct = productOptions.find(p => p.value === selectedProductId);

         if (selectedProduct) {
             const validUomIds = new Set();
             // Add base UOM ID from the selected product's data
             if (selectedProduct.base_uom_id) {
                 validUomIds.add(selectedProduct.base_uom_id);
             }
             // Add alternate UOM IDs from the selected product's 'product_units' array
             // ** THE FIX IS HERE: Access product_units from selectedProduct **
             if (Array.isArray(selectedProduct.product_units)) {
                 selectedProduct.product_units.forEach(unit => {
                      // Ensure the unit object has a uom_id before adding
                      if(unit.uom_id) {
                          validUomIds.add(unit.uom_id);
                      }
                 });
             }

             // Filter the main uomOptions list based on the collected valid IDs
             setFilteredUomOptions(uomOptions.filter(uom => validUomIds.has(uom.value)));
         } else {
             // If no product is selected, show all UOMs
             setFilteredUomOptions(uomOptions);
         }
     };

    // --- Input Handlers ---
    const handleProductChange = (selectedOption) => {
         const productId = selectedOption ? selectedOption.value : null;
         setCurrentRate(prev => ({ ...prev, product_id: productId, uom_id: null })); // Reset UOM
         setCurrentRateError(prev => ({ ...prev, product_id: null, uom_id: null }));
         filterUomsForProduct(productId); // Filter UOM dropdown
     };
    const handleUomChange = (selectedOption) => { setCurrentRate(prev => ({ ...prev, uom_id: selectedOption ? selectedOption.value : null })); };
    const handleRateTypeChange = (selectedOption) => { setCurrentRate(prev => ({ ...prev, rate_type_id: selectedOption ? selectedOption.value : null })); };
    const handleBasePriceChange = (e) => { setCurrentRate(prev => ({ ...prev, base_price: e.target.value })); };
    const handleDateChange = (date) => { setCurrentRate(prev => ({ ...prev, with_effect_from: date })); };

   // --- Adjustment Handlers ---
    const addAdjustment = () => {
        setCurrentRate(prev => ({
            ...prev,
            // Ensure adjustments is always an array before spreading
            adjustments: [
                ...(Array.isArray(prev.adjustments) ? prev.adjustments : []),
                // Add a new, empty adjustment object
                {
                    id: null, // No ID for new adjustments
                    hoa_id: null,
                    percentage: '', // Use empty string for controlled inputs
                    amount: '',     // Use empty string for controlled inputs
                    is_subtraction: false,
                    description: '' // Optional description field if needed
                }
            ],
        }));
    };

    const removeAdjustment = (index) => {
        setCurrentRate(prev => ({
            ...prev,
            // Ensure adjustments is an array before filtering
            adjustments: (Array.isArray(prev.adjustments) ? prev.adjustments : []).filter((_, i) => i !== index),
        }));
        // Optionally clear validation errors specific to this index if needed
        setCurrentRateError(prevErr => {
             const newAdjErrors = { ...(prevErr.adjustments || {}) };
             delete newAdjErrors[index]; // Remove errors for the deleted index
             return { ...prevErr, adjustments: newAdjErrors };
        });
    };

     const handleAdjustmentChange = (index, fieldName, value) => {
         setCurrentRate(prev => {
             // Ensure adjustments is an array
             const currentAdjustments = Array.isArray(prev.adjustments) ? prev.adjustments : [];
             const newAdjustments = [...currentAdjustments];

             // Safety check: ensure the object at the index exists
             if (!newAdjustments[index]) {
                  console.error(`Adjustment at index ${index} not found during update.`);
                  return prev; // Return previous state if index is invalid
             }

             let updatedValue = value;

             // Handle specific field types or logic
             if (fieldName === 'hoa_id') {
                 updatedValue = value ? parseInt(value, 10) : null; // Ensure value is integer or null
             } else if (fieldName === 'is_subtraction') {
                 updatedValue = typeof value === 'boolean' ? value : (value === 'true' || value === true); // Ensure boolean
             } else if (fieldName === 'percentage' || fieldName === 'amount') {
                 // Allow empty string or valid number format, prevent negative on type
                 updatedValue = value === '' ? '' : (value); // Keep as string for input control, validate on submit
             }

             // --- Logic to clear Percentage or Amount ---
             let adjustmentToUpdate = { ...newAdjustments[index], [fieldName]: updatedValue };

             if (fieldName === 'percentage' && updatedValue !== '') {
                  adjustmentToUpdate['amount'] = ''; // Clear amount if percentage is entered
             } else if (fieldName === 'amount' && updatedValue !== '') {
                  adjustmentToUpdate['percentage'] = ''; // Clear percentage if amount is entered
             }

             newAdjustments[index] = adjustmentToUpdate; // Assign the updated object back to the array

             return { ...prev, adjustments: newAdjustments };
         });

         // Clear specific validation error for this field when changed
          setCurrentRateError(prevErr => {
               const newAdjErrors = { ...(prevErr.adjustments || {}) };
               if (newAdjErrors[index]) {
                   delete newAdjErrors[index][fieldName]; // Remove error for the specific field
                   // Optional: remove index error if all field errors under it are gone
                   if (Object.keys(newAdjErrors[index]).length === 0) {
                        delete newAdjErrors[index];
                   }
               }
               return { ...prevErr, adjustments: newAdjErrors };
          });
     };


    // --- Save Handler ---
    const saveForm = (exitForm) => {
        setCurrentRateError({});
        setSaving(true);
        const apiCall = formType === 'create' ? createRate : updateRate;
        let formattedDate = null;
        if (currentRate.with_effect_from instanceof Date && !isNaN(currentRate.with_effect_from)) {
            formattedDate = format(currentRate.with_effect_from, 'yyyy-MM-dd');
        } else { /* ... error handling ... */ setSaving(false); return; }

        const cleanedAdjustments = (Array.isArray(currentRate.adjustments) ? currentRate.adjustments : [])
             .filter(adj => adj.hoa_id && (adj.percentage !== '' || adj.amount !== ''))
             .map(adj => ({
                 id: adj.id, hoa_id: adj.hoa_id,
                 percentage: adj.percentage === '' ? null : adj.percentage,
                 amount: adj.amount === '' ? null : adj.amount,
                 is_subtraction: adj.is_subtraction, description: adj.description
             }));

        const dataToSend = { ...currentRate, with_effect_from: formattedDate, adjustments: cleanedAdjustments };

        apiCall(dataToSend, (response) => {
            setSaving(false);
            if (response.rate) {
                // ... (success logic as before, update state for edit mode) ...
                 if (exitForm) { navigate('/franchisee_user/masters/rates', { state: { message: `Rate ${formType}d successfully!` } }); }
                 else if (formType === 'create') {
                     setCurrentRate({ product_id: null, uom_id: null, rate_type_id: null, base_price: '', with_effect_from: new Date(), adjustments: [] });
                     setFilteredUomOptions(uomOptions);
                     setToaster({ type: 'success', heading: 'Success', message: 'Rate added.' }); setShowToaster(true);
                 } else { // Edit + Save without exit
                      const adjustmentsFromServer = response.rate.adjustments;
                      const formattedAdjustments = Array.isArray(adjustmentsFromServer) ? adjustmentsFromServer.map(adj => ({...adj, percentage: adj.percentage ?? '', amount: adj.amount ?? '' })) : [];
                      setCurrentRate(prev => ({...prev, ...response.rate, with_effect_from: prev.with_effect_from, adjustments: formattedAdjustments})); // Keep date object, update adjustments
                     setToaster({ type: 'success', heading: 'Success', message: 'Rate updated.' }); setShowToaster(true);
                 }
            } else {
                handleApiError(response, setCurrentRateError);
                setToaster({ type: 'danger', heading: 'Error', message: `Could not ${formType} rate.` });
                setShowToaster(true);
            }
        });
    };

    // --- Reset Handler ---
    const handleReset = () => { /* ... (as before) ... */ };
    const getTitle = () => formType === 'create' ? 'Create New Rate' : 'Edit Rate';
    const isLoading = loadingDropdowns || loadingRateData;

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title={getTitle()}>
                         <li><a className="f-s-14 f-w-500" href="#">Masters</a></li>
                         <li><a className="f-s-14 f-w-500" href="/franchisee_user/masters/rates">Rates</a></li>
                         <li className="active"><a className="f-s-14 f-w-500" href="#">{capitalizeFirstLetter(formType)}</a></li>
                    </AppBreadCrumb>

                    {isLoading && <div className="loader_box"><div className="loader_14"></div></div>}

                    {!isLoading && (
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header"><h5>{getTitle()}</h5></div>
                                    <div className="card-body">
                                        <form className="app-form" onSubmit={(e) => e.preventDefault()}>
                                            {/* --- Main Rate Details --- */}
                                            <h6 className="mb-3">Rate Details</h6>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Product *</label>
                                                    <Select options={productOptions} value={productOptions.find(o => o.value === currentRate.product_id)} onChange={handleProductChange} placeholder="Select Product" isClearable isDisabled={loadingDropdowns}/>
                                                    <span className="text-danger">{currentRateError?.product_id}</span>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Unit of Measure *</label>
                                                    {/* ** FIX: UOM Dropdown enabled/disabled logic ** */}
                                                    <Select options={filteredUomOptions} value={filteredUomOptions.find(o => o.value === currentRate.uom_id)} onChange={handleUomChange} placeholder="Select UOM" isClearable isDisabled={!currentRate.product_id || loadingDropdowns} />
                                                    <span className="text-danger">{currentRateError?.uom_id}</span>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Rate Type *</label>
                                                    <Select options={rateTypeOptions} value={rateTypeOptions.find(o => o.value === currentRate.rate_type_id)} onChange={handleRateTypeChange} placeholder="Select Rate Type" isClearable isDisabled={loadingDropdowns}/>
                                                    <span className="text-danger">{currentRateError?.rate_type_id}</span>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Base Price *</label>
                                                    <input type="number" step="0.01" min="0" className="form-control" placeholder="Enter Base Price" value={currentRate.base_price || ''} onChange={handleBasePriceChange} required />
                                                    <span className="text-danger">{currentRateError?.base_price}</span>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">With Effect From *</label>
                                                     <DatePicker selected={currentRate.with_effect_from} onChange={handleDateChange} dateFormat="yyyy-MM-dd" className="form-control" wrapperClassName="d-block" />
                                                    <span className="text-danger">{currentRateError?.with_effect_from}</span>
                                                </div>
                                            </div>

                                            {/* --- Adjustments Section --- */}
                                            <hr className="my-4"/>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">Taxes / Adjustments (GST)</h6>
                                                <button type="button" className="btn btn-sm btn-outline-success" onClick={addAdjustment} disabled={loadingDropdowns}><i className="ti ti-plus me-1"></i> Add Adjustment</button>
                                            </div>
                                             <span className="text-danger d-block mb-2">{currentRateError?.adjustments && typeof currentRateError.adjustments === 'string' ? currentRateError.adjustments : ''}</span>

                                            {/* Mapping Adjustments */}
                                            {currentRate && Array.isArray(currentRate.adjustments) && currentRate.adjustments.map((adj, index) => (
                                                <div key={adj.id || `adj-${index}`} className="adjustment-block border p-3 mb-3 rounded position-relative bg-light">
                                                    <button type="button" className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1" onClick={() => removeAdjustment(index)} style={{ lineHeight: 1, padding: '0.2rem 0.4rem' }} title="Remove Adjustment">&times;</button>
                                                    <div className="row align-items-center">
                                                        {/* HOA Dropdown */}
                                                        <div className="col-md-4 mb-3">
                                                            <label className="form-label">Account (GST Head) *</label>
                                                            <Select
                                                                options={gstHoaOptions}
                                                                value={gstHoaOptions.find(o => o.value === adj.hoa_id)}
                                                                onChange={(selected) => handleAdjustmentChange(index, 'hoa_id', selected ? selected.value : null)}
                                                                placeholder="Select GST Head"
                                                                isClearable
                                                                isDisabled={loadingDropdowns}
                                                            />
                                                            <span className="text-danger">{currentRateError?.adjustments?.[index]?.hoa_id}</span>
                                                        </div>
                                                        {/* Percentage Input */}
                                                        <div className="col-md-3 mb-3">
                                                             <label className="form-label">Percentage</label>
                                                             <div className="input-group">
                                                                 <input type="number" step="0.01" min="0" className="form-control" placeholder="e.g., 9" value={adj.percentage || ''} onChange={(e) => handleAdjustmentChange(index, 'percentage', e.target.value)} disabled={!!adj.amount} /> {/* Disable if amount has value */}
                                                                 <span className="input-group-text">%</span>
                                                             </div>
                                                             <span className="text-danger">{currentRateError?.adjustments?.[index]?.percentage}</span>
                                                        </div>
                                                         {/* Amount Input */}
                                                        <div className="col-md-3 mb-3">
                                                             <label className="form-label">Amount</label>
                                                             <input type="number" step="0.01" min="0" className="form-control" placeholder="e.g., 10.00" value={adj.amount || ''} onChange={(e) => handleAdjustmentChange(index, 'amount', e.target.value)} disabled={!!adj.percentage}/> {/* Disable if percentage has value */}
                                                             <span className="text-danger">{currentRateError?.adjustments?.[index]?.amount}</span>
                                                        </div>
                                                         {/* Is Subtraction Checkbox */}
                                                        <div className="col-md-2 mb-3 d-flex align-items-end">
                                                            <div className="form-check form-switch">
                                                                <input className="form-check-input" type="checkbox" role="switch" id={`is_subtraction_${index}`} name="is_subtraction" checked={adj.is_subtraction || false} onChange={(e) => handleAdjustmentChange(index, 'is_subtraction', e.target.checked)} />
                                                                <label className="form-check-label" htmlFor={`is_subtraction_${index}`}>Subtract?</label>
                                                            </div>
                                                        </div>
                                                         {/* Hidden ID for updates */}
                                                         <input type="hidden" name="id" value={adj.id || ''} />
                                                          {/* Optional Description */}
                                                          {/* <div className="col-12 mb-3"> <label>Description</label> <input/> </div> */}
                                                    </div>
                                                </div>
                                            ))}


                                          {/* Action Buttons */}
                    <div className="col-12 mt-4">
                        <div className="text-end">
                            {formType === 'create' ? (
                                <>
                                    <button className="btn btn-outline-primary me-2 btn-sm" type="button" onClick={() => saveForm(false)} disabled={saving}>Save + Next</button>
                                    <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={saving}>Save + Exit</button>
                                    <button className="d-none d-md-inline-block btn btn-danger btn-sm" onClick={handleReset} type="button" disabled={saving}>Reset</button>
                                </>
                            ) : (
                                <>
                                    <button className="btn btn-primary me-2 btn-sm" type="button" onClick={() => saveForm(true)} disabled={saving}>Update</button>
                                    <button className="btn btn-secondary me-2 btn-sm" type="button" onClick={handleReset} disabled={saving}>Reset</button>
                                    <button className="btn btn-danger btn-sm" type="button" onClick={() => navigate('/franchisee_user/masters/rates')} disabled={saving}>Cancel</button>
                                </>
                            )}
                        </div>
                    </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
};

export default RatesForm;